import Image from '@src/quince-site-common/src/domains/Image'
import ProductOptionValue from '@src/quince-site-common/src/domains/ProductOption'
import RichText from '@src/quince-site-common/src/domains/RichText'

import { AddOnProductOptionGroup } from '../AddOnProductOptionGroup'
import ContentfulInternationalSizeConversionGuide from '../contentful/contentful-international-size-conversion-guide'
import { ContentfulProductMeasuringGuide } from '../contentful/ContentfulMeasuringGuide'
import { ContentfulSizeChart } from '../contentful/ContentfulProduct'
import CustomSection from '../CustomSection'
import { Gender } from '../Gender'
import { MergedSizeChart } from '../MergedSizeChart'
import ProductExperimentImages from '../product-experiment-images'
import { ProductClassification } from '../productClassification'
import { ProductCustomField } from '../ProductCustomField'
import ProductExperimentVideo from '../ProductExperimentVideo'
import ProductHierarchy from '../ProductHierarchy'
import ProductReview, { ProductReviewSummary } from '../ProductReview'
import ProductTransparentPricing from '../ProductTransparentPricing'
import ProductVariant from '../ProductVariant'
import ProductVideo from '../ProductVideo'
import { ISubscription } from '../Subscription'
import { ISwatch } from '../Swatch'

import BaseProduct, { IBaseProduct } from './BaseProduct'
import PRODUCT_DOMAIN_TYPES from './ProductDomainTypes'
import { ProductRecommendation } from './ProductRecommendation'

import type { Document } from '@contentful/rich-text-types'

export interface IProduct extends IBaseProduct<ProductVariant> {
  readonly contentfulProductId: string
  readonly gender: Gender
  readonly videos: ProductVideo[]
  readonly description?: string | undefined
  readonly details?: RichText | undefined
  readonly disclaimer?: string | undefined | null
  readonly sizeAndFit?: RichText | undefined
  readonly productTypeName: string
  readonly careAndMaintenance?: RichText | undefined
  readonly customFields?: any[] | undefined
  readonly productReviewSummary?: ProductReviewSummary | undefined
  readonly productReviews?: ProductReview[] | undefined | null
  readonly desktopSections?: CustomSection[] | undefined
  readonly mobileSections?: CustomSection[] | undefined
  readonly transparentPricing?: ProductTransparentPricing | undefined
  readonly spHandle?: string | undefined
  readonly isMadeToOrder?: boolean
  readonly madeToOrderText?: Document | undefined | null
  readonly shippingInformation?: string | undefined | null
  readonly shippingMetaInfoText?: string | undefined | null
  readonly sizeChart?: ContentfulSizeChart | undefined
  readonly measuringGuides?: ContentfulProductMeasuringGuide[] | undefined
  readonly internationalSizeConversionGuides?: ContentfulInternationalSizeConversionGuide[] | undefined
  readonly mergedSizeChart?: MergedSizeChart[] | undefined
  readonly vendorCodes: string[]
  readonly hideBrandComparison?: boolean
  readonly hasRestockingFee?: boolean
  readonly hierarchy?: ProductHierarchy[] | undefined
  readonly addOnProductOptionGroups?: AddOnProductOptionGroup[]
  readonly productDetailIcons?: Image[]
  readonly subClassId?: boolean
  readonly new_color_options?: ProductOptionValue[]
  readonly classification?: ProductClassification
  readonly swatch?: ISwatch
  readonly isWhiteGloveDeliveryAvailable?: boolean
  readonly benefits?: string
  readonly ingredients?: string
  readonly notes?: string
  readonly installation?: string
  readonly specs?: string
  readonly dimensions?: string
  readonly hideSavings?: boolean
  readonly productRecommendations: ProductRecommendation
  readonly offerMessage?: string
  readonly feeding?: string
  readonly shelfLife?: string
  readonly shipping?: string
  readonly subscriptionEnabled?: boolean
  readonly subscription?: ISubscription
  readonly experimentVideos?: ProductExperimentVideo
  readonly experimentImages?: ProductExperimentImages
}

export default class Product extends BaseProduct<ProductVariant> implements IProduct {
  readonly contentfulProductId: string
  readonly desktopSections: CustomSection[]
  readonly mobileSections: CustomSection[]
  readonly gender: Gender
  readonly videos: ProductVideo[]
  readonly description: string | undefined
  readonly details: RichText | undefined
  readonly disclaimer?: string | undefined | null
  readonly sizeAndFit: RichText | undefined
  readonly productTypeName: string
  readonly careAndMaintenance: RichText | undefined
  readonly productReviewSummary: ProductReviewSummary | undefined
  readonly customFields: ProductCustomField[] | undefined
  readonly productReviews: ProductReview[] | null
  readonly transparentPricing: ProductTransparentPricing | undefined
  readonly spHandle: string | undefined
  readonly isMadeToOrder: boolean
  readonly sizeChart: ContentfulSizeChart | undefined
  readonly mergedSizeChart?: MergedSizeChart[] | undefined
  readonly measuringGuides?: ContentfulProductMeasuringGuide[] | undefined
  readonly internationalSizeConversionGuides?: ContentfulInternationalSizeConversionGuide[] | undefined
  readonly vendorCodes: string[]
  readonly hideBrandComparison?: boolean
  readonly hasRestockingFee?: boolean
  readonly hierarchy?: ProductHierarchy[] | undefined
  readonly addOnProductOptionGroups?: AddOnProductOptionGroup[]
  readonly subClassId?: boolean
  // TODO: In future, organise below things better, for now, keeping how it is right now.
  readonly madeToOrderText: Document | undefined | null
  readonly shippingInformation: string | undefined | null
  readonly shippingMetaInfoText: string | undefined | null
  readonly traditionalDiscountText?: RichText
  readonly productDetailIcons?: Image[]
  override readonly classification?: ProductClassification
  readonly swatch?: ISwatch
  override readonly isWhiteGloveDeliveryAvailable?: boolean
  readonly benefits?: string
  readonly ingredients?: string
  readonly notes?: string
  readonly installation?: string
  readonly specs?: string
  readonly dimensions?: string
  readonly hideSavings?: boolean
  readonly productRecommendations: ProductRecommendation
  readonly offerMessage?: string
  readonly feeding?: string
  readonly shelfLife?: string
  readonly shipping?: string
  readonly subscriptionEnabled?: boolean
  readonly subscription?: ISubscription
  readonly experimentVideos?: ProductExperimentVideo
  readonly experimentImages?: ProductExperimentImages

  constructor(iProduct: IProduct) {
    super(iProduct, PRODUCT_DOMAIN_TYPES.PRODUCT)
    const {
      contentfulProductId,
      gender,
      videos,
      description,
      details,
      disclaimer,
      productTypeName,
      careAndMaintenance,
      productReviewSummary,
      customFields = [],
      productReviews = [],
      desktopSections = [],
      mobileSections = [],
      transparentPricing,
      spHandle,
      isMadeToOrder = false,
      madeToOrderText,
      shippingInformation,
      shippingMetaInfoText,
      sizeChart,
      mergedSizeChart,
      measuringGuides = [],
      internationalSizeConversionGuides = [],
      vendorCodes,
      hideBrandComparison,
      hasRestockingFee,
      hierarchy,
      addOnProductOptionGroups,
      productDetailIcons,
      sizeAndFit,
      subClassId,
      classification,
      swatch,
      isWhiteGloveDeliveryAvailable,
      benefits,
      ingredients,
      notes,
      installation,
      specs,
      dimensions,
      hideSavings,
      productRecommendations,
      offerMessage,
      feeding,
      shelfLife,
      shipping,
      subscriptionEnabled,
      subscription,
      experimentVideos,
      experimentImages,
    } = iProduct

    this.contentfulProductId = contentfulProductId
    this.desktopSections = desktopSections
    this.mobileSections = mobileSections
    this.gender = gender
    this.videos = videos
    this.description = description
    this.details = details
    this.disclaimer = disclaimer
    this.sizeAndFit = sizeAndFit
    this.productTypeName = productTypeName
    this.careAndMaintenance = careAndMaintenance
    this.productReviewSummary = productReviewSummary
    this.customFields = customFields
    this.productReviews = productReviews
    this.transparentPricing = transparentPricing
    this.spHandle = spHandle
    this.isMadeToOrder = isMadeToOrder
    this.madeToOrderText = madeToOrderText
    this.shippingInformation = shippingInformation
    this.shippingMetaInfoText = shippingMetaInfoText
    this.sizeChart = sizeChart
    this.mergedSizeChart = mergedSizeChart
    this.measuringGuides = measuringGuides
    this.internationalSizeConversionGuides = internationalSizeConversionGuides
    this.vendorCodes = vendorCodes
    this.hideBrandComparison = hideBrandComparison
    this.hasRestockingFee = hasRestockingFee
    this.hierarchy = hierarchy
    this.addOnProductOptionGroups = addOnProductOptionGroups
    this.productDetailIcons = productDetailIcons
    this.subClassId = subClassId
    this.classification = classification
    this.swatch = swatch
    this.isWhiteGloveDeliveryAvailable = isWhiteGloveDeliveryAvailable
    this.benefits = benefits
    this.ingredients = ingredients
    this.notes = notes
    this.installation = installation
    this.specs = specs
    this.dimensions = dimensions
    this.hideSavings = hideSavings
    this.productRecommendations = productRecommendations
    this.offerMessage = offerMessage
    this.feeding = feeding
    this.shelfLife = shelfLife
    this.shipping = shipping
    this.subscriptionEnabled = subscriptionEnabled
    this.subscription = subscription
    this.experimentVideos = experimentVideos
    this.experimentImages = experimentImages
    return {
      ...this,
    }
  }
}
